/**
 * usePresenceTracker — pings the backend with the user's currently-open
 * task so Team Pulse can show an accurate "Working on" label.
 *
 * Contract:
 *   - Caller supplies `taskId` (null when no task is open / detail sheet
 *     is closed / user is on a non-task page).
 *   - Hook POSTs /api/presence/viewing-task on every change.
 *   - While `taskId` is non-null, hook re-pings every 30 s to keep the
 *     server-side TTL (120 s) fresh.
 *   - On unmount, hook clears presence so the next pulse refresh shows
 *     "Not on a task".
 *
 * Coalesces rapid changes via a 250 ms debounce so a user clicking through
 * a list of tasks doesn't slam the API.
 */
import { useEffect, useRef } from 'react';
import { presenceAPI } from './api';

const HEARTBEAT_MS = 30 * 1000;
const DEBOUNCE_MS = 250;

export function usePresenceTracker(workspaceId, taskId) {
  const lastSentRef = useRef(null);
  const heartbeatRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!workspaceId) return undefined;
    const nextTaskId = taskId || null;

    // Debounce rapid task-id changes (e.g. clicking through a task list).
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (lastSentRef.current === nextTaskId) {
        // Same task — just keep the heartbeat going.
        return;
      }
      lastSentRef.current = nextTaskId;
      presenceAPI.viewingTask(workspaceId, nextTaskId).catch(() => {});
    }, DEBOUNCE_MS);

    // Heartbeat (only while a task is open).
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (nextTaskId) {
      heartbeatRef.current = setInterval(() => {
        presenceAPI.viewingTask(workspaceId, nextTaskId).catch(() => {});
      }, HEARTBEAT_MS);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [workspaceId, taskId]);

  // On final unmount, clear presence so the team pulse drops it.
  useEffect(() => {
    return () => {
      if (workspaceId && lastSentRef.current) {
        // Fire-and-forget; ok if it fails on unload.
        try { presenceAPI.viewingTask(workspaceId, null); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default usePresenceTracker;
